// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { createReducer } from 'redux-act';

import {
  selectPricingInfos,
  type PricingInfos,
  type Currency,
} from '@gandi/billing';
import type { ApplicationSummary } from '@gandi/redux-resource-simplehosting';
import { createResource } from '@gandi/resource/redux';

import { createLogger } from 'gandi.redux-logger';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { FullState } from '../../types';
import type {
  CurrentSize,
  LegacySize,
  HostingPlan,
  InstancePrices,
  State,
} from '../types';

const resource = createResource('prices.webhosting.instances');
const ENDPOINT = '/simplehosting/prices';

const toQueryStringParams = (obj: PricingInfos) =>
  ((Object.entries(obj): any): Array<[string, string | Currency]>)
    .map(([key, val]) => val && `${key}=${val}`)
    .join('&');

const selectInstancesPrices = (
  state: FullState,
  label: string,
): Array<InstancePrices> => state.webhosting?.[label];

export function useInstancesPrices(): {
  instancesPrices: Array<InstancePrices>,
  isFetching: boolean,
  isLoaded: boolean,
  isFailure: boolean,
  isToRefetch: boolean,
} {
  const dispatch = useDispatch();

  // $FlowFixMe[incompatible-call]
  const pricingInfos = useAppSelector(selectPricingInfos);
  const url = `${ENDPOINT}?${toQueryStringParams(pricingInfos)}`;

  const isFetching = useAppSelector(state =>
    resource.selectIsFetching(state, { label: url }),
  );
  const isLoaded = useAppSelector(state =>
    resource.selectIsLoaded(state, { label: url }),
  );
  const isFailure = useAppSelector(state =>
    resource.selectIsFailure(state, {
      label: url,
    }),
  );
  const isToRefetch = useAppSelector(state =>
    resource.selectIsToRefetch(state, { label: url }),
  );

  const instancesPrices = useAppSelector(state =>
    selectInstancesPrices(state, url),
  );
  const shouldFetch = useAppSelector(state =>
    resource.selectShouldFetch(state, { label: url }),
  );

  React.useEffect(() => {
    if (shouldFetch) {
      try {
        dispatch(
          // url as url and label
          resource.fetch(url, { label: url }),
        );
      } catch (e) {
        const logger = createLogger('WebHoting/modules/prices');
        logger.error({ e }, 'Error while fetching instances prices.');
      }
    }
  }, [dispatch, shouldFetch, url]);

  return {
    instancesPrices,
    isFetching,
    isLoaded,
    isFailure,
    isToRefetch,
  };
}

const legacySizes: Array<LegacySize> = ['s', 'm', 'l', 'xl'];

const legacyToCurrentSizes: { +[key: LegacySize]: CurrentSize, ... } = {
  s: 'starter',
  m: 'advanced',
  l: 'pro',
  xl: 'business',
};

function getIncludedSizes(
  sizes: Array<LegacySize>,
  requirement: ?LegacySize,
): Array<CurrentSize> {
  if (!requirement) {
    return sizes.map(s => legacyToCurrentSizes[s]);
  }

  const filteredSizes = sizes.slice(
    sizes.findIndex(size => size === requirement),
  );
  return filteredSizes.map(s => legacyToCurrentSizes[s]);
}

export function useIsDiscountPeriod(): boolean {
  const { instancesPrices, isLoaded: instancesPricesLoaded } =
    useInstancesPrices();

  if (!instancesPricesLoaded) return false;

  return instancesPrices?.some(
    p =>
      p.normal_price_before_taxes !== undefined &&
      // blackfriday 2022
      p.start_at === '2022-11-24T00:00:00Z' &&
      p.end_at === '2022-11-29T00:00:00Z',
  );
}

export function usePlanPrices(
  plan: HostingPlan,
  application?: ?ApplicationSummary,
): void | Array<InstancePrices> {
  const { instancesPrices, isLoaded: instancesPricesLoaded } =
    useInstancesPrices();

  if (!instancesPricesLoaded) return undefined;

  // the catalog set yearly with
  // - min_duration = 12
  // - max_duration = 12
  // - duration_unit = 'month'
  // and monthly with
  // - min_duration = 1
  // - max_duration = 1
  // - duration_unit = 'month'
  const plan_min_duration = plan === 'monthly' ? 1 : 12;

  const sizesToInclude = getIncludedSizes(
    legacySizes,
    // $FlowFixMe[incompatible-call]
    application?.requirements?.[0].instance_minimum_size,
  );

  return instancesPrices
    .filter(price => price.min_duration === plan_min_duration)
    .filter(price => sizesToInclude.includes(price.product.name))
    .sort((a, b) => a.price_before_taxes - b.price_before_taxes); // sort by price
}

const initialState: State = {};

export const reducer = (createReducer(
  {
    [resource.fetch.success.getType()]: (
      state,
      { response: { data }, label },
    ) => ({
      ...state,
      [label]: data,
    }),
  },
  initialState,
): (state: State, payload?: mixed, meta?: mixed) => State);
